import { useQuery, useMutation } from 'convex/react';
import { api } from '../../api';
import { useUserDevice } from './useUserDevice';

interface AppRestrictions {
  allowedCategories: string[];
  whitelist: string[];
  blacklist: string[];
}

export const useAppRestrictions = () => {
  const { device } = useUserDevice();

  // Fetch device details to get deviceConfig
  const deviceDetails = useQuery(
    api.devices.get.details,
    device?._id ? { device: device._id as any } : "skip"
  );

  // Fetch device-specific policies and categories
  const devicePolicies = useQuery(
    api.devices.policies.get,
    deviceDetails?.device?.deviceConfig ? { 
      deviceConfig: deviceDetails.device.deviceConfig 
    } : "skip"
  );

  // Fetch rules for this device
  const rules = useQuery(
    api.rules.list.all,
    device?.jssid ? {
      paginationOpts: { cursor: "", numItems: 100 }
    } : "skip"
  );

  // Transform Convex data into our expected format
  const restrictions: AppRestrictions = {
    allowedCategories: devicePolicies?.categories ? 
      Object.entries(devicePolicies.categories)
        .filter(([_, status]) => status !== false)
        .map(([category]) => category) : [],
    whitelist: devicePolicies?.allowlist ? 
      Object.entries(devicePolicies.allowlist)
        .filter(([_, status]) => status !== false)
        .map(([url]) => url) : [],
    blacklist: devicePolicies?.blocklist ? 
      Object.entries(devicePolicies.blocklist)
        .filter(([_, status]) => status !== false)
        .map(([url]) => url) : []
  };

  const updateCategoryRestriction = async (categoryId: string, allowed: boolean) => {
    if (!deviceDetails?.device?.deviceConfig) return;

    try {
      // Update the category restriction using the policies update mutation
      const currentCategories = devicePolicies?.categories || {};
      const updatedCategories = {
        ...currentCategories,
        [categoryId]: allowed
      };
      
      console.log('Update category restriction:', categoryId, allowed);
      // This would need the proper Convex mutation based on your schema
      // await updatePoliciesMutation({ 
      //   deviceConfig: deviceDetails.device.deviceConfig,
      //   policyOverrides: updatedCategories 
      // });
    } catch (error) {
      console.error('Failed to update category restriction:', error);
      throw error;
    }
  };

  const addToWhitelist = async (appName: string) => {
    if (!device?.jssid) return;

    try {
      // For now, log the action - would need proper Convex rules mutations
      console.log('Add to whitelist:', appName, 'for device:', device.jssid);
      // await addRuleMutation({ ... proper rule data structure ... });
    } catch (error) {
      console.error('Failed to add to whitelist:', error);
      throw error;
    }
  };

  const removeFromWhitelist = async (appName: string) => {
    if (!device?.jssid) return;

    try {
      // For now, log the action - would need proper Convex rules mutations
      console.log('Remove from whitelist:', appName);
      // const ruleToDelete = rules?.page?.find(rule => rule.protocol === 'socket' && rule.target === appName);
      // if (ruleToDelete) await deleteRuleMutation({ rule: ruleToDelete._id });
    } catch (error) {
      console.error('Failed to remove from whitelist:', error);
      throw error;
    }
  };

  const addToBlacklist = async (appName: string) => {
    if (!device?.jssid) return;

    try {
      // For now, log the action - would need proper Convex rules mutations
      console.log('Add to blacklist:', appName, 'for device:', device.jssid);
      // await addRuleMutation({ ... proper rule data structure ... });
    } catch (error) {
      console.error('Failed to add to blacklist:', error);
      throw error;
    }
  };

  const removeFromBlacklist = async (appName: string) => {
    if (!device?.jssid) return;

    try {
      // For now, log the action - would need proper Convex rules mutations
      console.log('Remove from blacklist:', appName);
      // const ruleToDelete = rules?.page?.find(rule => rule.protocol === 'webkit' && rule.target === appName);
      // if (ruleToDelete) await deleteRuleMutation({ rule: ruleToDelete._id });
    } catch (error) {
      console.error('Failed to remove from blacklist:', error);
      throw error;
    }
  };

  return {
    restrictions,
    updateCategoryRestriction,
    addToWhitelist,
    removeFromWhitelist,
    addToBlacklist,
    removeFromBlacklist,
    isLoading: deviceDetails === undefined || devicePolicies === undefined,
    refetch: () => {
      // Convex handles refetching automatically
    },
  };
};